import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Check, Plus, Trophy, X } from 'lucide-react';
import { ChallengeMe, ChallengeRideDraft, ChallengeSummary, profileChallengeModel, rideSavedModel } from '@radevents/shared';
import { RideForm } from './ChallengeParts';
import { CountUp, MilestoneBurst, usePrefersReducedMotion } from './Motion';
import { ORGANIZER_ACCESS_CSS } from '../organizer/OrganizerAccessDialog';
import { messageOf } from './messageOf';
import { useDialogFocus } from './useDialogFocus';
import './winterchallenge.css';

/** Standard: über die API. Erst bei Bedarf geladen, damit Startseite und Tests kein Axios mitziehen. */
async function addRideViaApi(draft: ChallengeRideDraft): Promise<ChallengeMe> {
  const m = await import('../../services/challengeService');
  return m.default.addRide(draft);
}

/**
 * Kilometer eintragen, ohne die Seite zu verlassen: von der Startseite (Werbeplatz) und aus dem
 * Profil. Vorher führte der Knopf auf `/winterchallenge?eintragen=1`; dort stand das Formular
 * zwar offen, aber zwischen Kopf, Rangliste und Spielregeln (Phil, 25.10.2026). Hier steht nur
 * das Formular, und nach dem Speichern sieht man, was die Fahrt gebracht hat.
 *
 * Bearbeiten und Löschen einzelner Fahrten bleibt auf der Challenge-Seite.
 */
export function QuickRideDialog({ me, summary, today, onClose, onSaved, save = addRideViaApi, preview, inline = false }: {
  me: ChallengeMe;
  summary: ChallengeSummary;
  today: string;
  onClose: () => void;
  /** Neuer Stand nach dem Speichern, damit die Seite dahinter ihn gleich zeigt. */
  onSaved?: (next: ChallengeMe) => void;
  save?: (draft: ChallengeRideDraft) => Promise<ChallengeMe>;
  /** Design-Lab: gespeicherten Stand vorgeben bzw. eine Serverantwort zeigen. */
  preview?: { saved?: ChallengeMe; error?: string };
  /** Design-Lab: an Ort und Stelle zeichnen statt in `document.body`. */
  inline?: boolean;
}) {
  const [current, setCurrent] = useState(me);
  const [saved, setSaved] = useState<{ before: ChallengeMe; after: ChallengeMe } | null>(
    preview?.saved ? { before: me, after: preview.saved } : null,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(preview?.error ?? null);
  const [round, setRound] = useState(0);

  // Fokus ins Fenster, Tab bleibt drin, Escape schließt, danach zurück auf den Öffner.
  const panel = useDialogFocus<HTMLDivElement>(onClose);

  const submit = async (draft: ChallengeRideDraft) => {
    setSaving(true);
    setError(null);
    try {
      const next = await save(draft);
      setSaved({ before: current, after: next });
      setCurrent(next);
      onSaved?.(next);
    } catch (err) {
      setError(messageOf(err, 'Die Fahrt wurde nicht gespeichert.'));
    } finally {
      setSaving(false);
    }
  };

  const status = profileChallengeModel(current, summary, today);

  // Über `document.body`: die Kachel im Profil liegt in einer Karte, deren Rahmen das Fenster sonst beschneidet.
  const dialog = (
    <div className="oa-backdrop" role="dialog" aria-modal="true" aria-labelledby="wc-quick-title"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{ORGANIZER_ACCESS_CSS}</style>
      <div className="oa-dialog wc-quick" ref={panel} tabIndex={-1}>
        <div className="oa-top">
          <span className="oa-step">{summary.title}</span>
          <button type="button" className="oa-icon-btn" onClick={onClose} aria-label="Schließen"><X size={16} /></button>
        </div>
        {saved ? (
          <RideSaved key={round} before={saved.before} after={saved.after} summary={summary} today={today}
            onMore={() => { setSaved(null); setRound(r => r + 1); }} onClose={onClose} />
        ) : (
          <>
            <h2 id="wc-quick-title" className="oa-title">Kilometer eintragen</h2>
            <p className="wc-quick-status">
              <strong>{status.km} km</strong>
              <span>{status.detail}</span>
            </p>
            <RideForm key={round} inDialog summary={summary} today={today} saving={saving} serverError={error}
              onSubmit={submit} onCancel={onClose} />
          </>
        )}
      </div>
    </div>
  );
  return inline ? dialog : createPortal(dialog, document.body);
}

/** Der Moment nach dem Speichern: was dazukam, der neue Stand, und ob ein Meilenstein fiel. */
export function RideSaved({ before, after, summary, today, onMore, onClose }: {
  before: ChallengeMe;
  after: ChallengeMe;
  summary: ChallengeSummary;
  today: string;
  onMore: () => void;
  onClose: () => void;
}) {
  const m = rideSavedModel(before, after, summary, today);
  const reduced = usePrefersReducedMotion();
  const [width, setWidth] = useState(reduced ? m.percentAfter : m.percentBefore);
  useEffect(() => {
    if (reduced) return;
    // Erst den alten Stand zeichnen, dann den neuen: der Übergang in `.wc-bar-fill` zeigt den Zuwachs.
    const id = window.setTimeout(() => setWidth(m.percentAfter), 250);
    return () => window.clearTimeout(id);
  }, [m.percentAfter, reduced]);

  return (
    <div className={`wc-saved${m.milestone != null ? ' is-milestone' : ''}`}>
      <span className="wc-burst-host">
        <span className="wc-saved-badge" aria-hidden>
          {m.milestone != null ? <Trophy size={26} /> : <Check size={22} />}
        </span>
        {m.milestone != null && <MilestoneBurst />}
      </span>
      <h2 id="wc-quick-title" className="oa-title wc-saved-title">{m.headline}</h2>
      <p className="wc-saved-added">
        {m.added >= 0 ? '+' : ''}<CountUp value={m.added} ms={700} format={n => (Math.round(n * 10) / 10).toLocaleString('de-DE')} /> km
      </p>
      <div className="wc-saved-now">
        <span>Jetzt <strong>{m.km} km</strong></span>
        <span>{m.detail}</span>
      </div>
      <span className="wc-bar"><span className="wc-bar-fill" style={{ width: `${width}%` }} /></span>
      <div className="wc-form-actions wc-saved-actions">
        <button type="button" className="wc-btn wc-btn--ghost" onClick={onMore}><Plus size={16} aria-hidden />Noch eine Fahrt</button>
        <button type="button" className="wc-btn" onClick={onClose}>Fertig</button>
      </div>
      <Link to="/winterchallenge" className="wc-saved-link" onClick={onClose}>Alle Fahrten und Ranglisten</Link>
    </div>
  );
}

/**
 * Startseite: der Werbeplatz zeigt immer nur die aktuelle Anzeige und wechselt sie aus. Hinge das
 * Fenster in der Anzeige, verschwände es beim nächsten Wechsel samt Eingabe. Deshalb öffnet die
 * Anzeige es über `openQuickRide`, gezeichnet wird es von `QuickRideHost` außerhalb des Wechsels.
 */
type QuickRideRequest = { me: ChallengeMe; summary: ChallengeSummary; today: string; onSaved?: (next: ChallengeMe) => void };

let pendingRequest: QuickRideRequest | null = null;
const hostListeners = new Set<() => void>();

export function openQuickRide(request: QuickRideRequest) {
  pendingRequest = request;
  hostListeners.forEach(l => l());
}

export function QuickRideHost() {
  const [request, setRequest] = useState<QuickRideRequest | null>(pendingRequest);
  useEffect(() => {
    const listener = () => setRequest(pendingRequest);
    hostListeners.add(listener);
    return () => { hostListeners.delete(listener); };
  }, []);
  if (!request) return null;
  return <QuickRideDialog {...request} onClose={() => { pendingRequest = null; setRequest(null); }} />;
}
